import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Send, Plus, Bot, User, MessageSquare, HelpCircle, ArrowLeft, ScrollText } from 'lucide-react';

import api from '../services/api';
import { useAuth } from '../context/AuthContext';

import toast from 'react-hot-toast';

import './AgentChat.css';



function parseActionItems(raw) {

  if (!raw) return [];

  if (Array.isArray(raw)) return raw;

  try {

    return JSON.parse(raw);

  } catch {

    return [];

  }

}



function getConversationPathKind(conv) {
  const kind = String(conv.conversation_kind || '');
  if (/seller/i.test(kind)) return 'seller';
  if (/buyer/i.test(kind)) return 'buyer';
  return 'unknown';
}

export default function Chat() {
  const { agent } = useAuth();
  const aiName = agent?.assistant_name || 'Ava';
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl === 'anonymous' ? 'anonymous' : 'identified';
  const pathFromUrl = searchParams.get('path');
  const initialPath = ['buyer', 'seller', 'all'].includes(pathFromUrl) ? pathFromUrl : 'all';

  const [conversations, setConversations] = useState([]);

  const [loadingList, setLoadingList] = useState(true);

  const [activeConv, setActiveConv] = useState(null);

  const [detailMode, setDetailMode] = useState(null); // null | 'transcript' | 'qa'

  const [messages, setMessages] = useState([]);

  const [input, setInput] = useState('');

  const [sending, setSending] = useState(false);

  const [starting, setStarting] = useState(false);

  const messagesEndRef = useRef(null);



  const [qaHistory, setQaHistory] = useState([]);

  const [qaQuestion, setQaQuestion] = useState('');

  const [qaAsking, setQaAsking] = useState(false);

  const [inboxTab, setInboxTab] = useState(initialTab); // identified | anonymous
  const [pathFilter, setPathFilter] = useState(initialPath); // all | buyer | seller

  const setInboxTabWithUrl = (tab) => {
    setInboxTab(tab);
    const next = {};
    if (tab === 'anonymous') next.tab = 'anonymous';
    if (pathFilter !== 'all') next.path = pathFilter;
    setSearchParams(next, { replace: true });
  };

  const setPathFilterWithUrl = (path) => {
    setPathFilter(path);
    const next = {};
    if (inboxTab === 'anonymous') next.tab = 'anonymous';
    if (path !== 'all') next.path = path;
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (tabFromUrl === 'anonymous' || tabFromUrl === 'identified') {
      setInboxTab(tabFromUrl === 'anonymous' ? 'anonymous' : 'identified');
    }
  }, [tabFromUrl]);

  useEffect(() => {
    if (['buyer', 'seller', 'all'].includes(pathFromUrl)) {
      setPathFilter(pathFromUrl);
    }
  }, [pathFromUrl]);



  const fetchConversations = useCallback(() => {
    setLoadingList(true);
    return api.get('/conversations')
      .then(res => {
        const convs = res.data.conversations || [];
        setConversations(convs);
        return convs;
      })
      .catch((err) => {
        toast.error('Failed to load conversations');
        console.error(err);
        return [];
      })
      .finally(() => setLoadingList(false));
  }, []);



  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {

    if (activeConv && detailMode === 'transcript') {

      api.get(`/conversations/${activeConv.id}/messages`)

        .then(res => setMessages(res.data.messages))

        .catch(console.error);

    }

  }, [activeConv, detailMode]);



  useEffect(() => {

    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  }, [messages, sending]);



  const startNewConversation = async () => {

    setStarting(true);

    try {

      const res = await api.post('/conversations', { channel: 'web_chat' });

      const conv = res.data.conversation;

      setConversations(c => [conv, ...c]);

      openTranscript(conv);

      setMessages([{ id: 'greeting', role: 'assistant', content: res.data.greeting_message, created_at: new Date() }]);

    } catch {

      toast.error('Failed to start conversation');

    } finally {

      setStarting(false);

    }

  };



  const openTranscript = (conv) => {

    setActiveConv(conv);

    setDetailMode('transcript');

    setQaHistory([]);

    setQaQuestion('');

  };



  const openQa = async (conv) => {

    setActiveConv(conv);

    setDetailMode('qa');

    setQaQuestion('');

    try {

      const res = await api.get(`/conversations/${conv.id}/qa`);

      setQaHistory(res.data.qa || []);

    } catch {

      setQaHistory([]);

    }

  };



  const closeDetail = () => {

    setActiveConv(null);

    setDetailMode(null);

    setMessages([]);

  };

  useEffect(() => {
    const convId = searchParams.get('conv');
    if (!convId || loadingList) return;
    const conv = conversations.find((c) => c.id === convId);
    if (conv) {
      openTranscript(conv);
      return;
    }
    let cancelled = false;
    api.get(`/conversations/${convId}`)
      .then((res) => {
        const found = res.data?.conversation;
        if (!found || cancelled) return;
        setConversations((list) => (list.some((c) => c.id === found.id) ? list : [found, ...list]));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [conversations, loadingList, searchParams]);

  const sendMessage = async () => {

    if (!input.trim() || !activeConv || sending) return;

    const userMessage = { id: Date.now(), role: 'user', content: input, created_at: new Date() };

    setMessages(m => [...m, userMessage]);

    setInput('');

    setSending(true);

    try {

      const res = await api.post(`/conversations/${activeConv.id}/message`, { content: input });

      setMessages(m => [...m, { id: Date.now() + 1, role: 'assistant', content: res.data.message, created_at: new Date() }]);

    } catch {

      toast.error('Failed to send message');

      setMessages(m => m.filter(msg => msg.id !== userMessage.id));

    } finally {

      setSending(false);

    }

  };



  const handleKeyDown = (e) => {

    if (e.key === 'Enter' && !e.shiftKey) {

      e.preventDefault();

      sendMessage();

    }

  };



  const askQuestion = async () => {

    if (!qaQuestion.trim() || !activeConv || qaAsking) return;

    const question = qaQuestion.trim();

    setQaQuestion('');

    setQaAsking(true);

    try {

      const res = await api.post(`/conversations/${activeConv.id}/ask`, { question });

      setQaHistory(h => [...h, { id: Date.now(), question, answer: res.data.answer, created_at: new Date() }]);

    } catch {

      toast.error('Failed to get answer');

    } finally {

      setQaAsking(false);

    }

  };



  const isIdentifiedConversation = (conv) => (
    conv.is_registered_client
    || conv.client_email
    || conv.lead_first_name
    || conv.lead_id
  );

  const displayName = (conv) => {
    if (conv.lead_first_name) {
      return `${conv.lead_first_name} ${conv.lead_last_name || ''}`.trim();
    }
    if (conv.client_email) return conv.client_email.split('@')[0];
    return 'Anonymous visitor';
  };

  const pathFilteredConvs = conversations.filter((c) => {
    if (pathFilter === 'all') return true;
    const pk = getConversationPathKind(c);
    if (pathFilter === 'seller') return pk === 'seller';
    return pk !== 'seller';
  });
  const identifiedConvs = pathFilteredConvs.filter(isIdentifiedConversation);
  const anonymousConvs = pathFilteredConvs.filter((c) => !isIdentifiedConversation(c));
  const filteredConvs = inboxTab === 'identified' ? identifiedConvs : anonymousConvs;
  const sellerCount = conversations.filter((c) => getConversationPathKind(c) === 'seller').length;
  const buyerCount = conversations.length - sellerCount;

  const renderInboxCard = (conv) => {
    const actionItems = parseActionItems(conv.action_items);
    const anonymous = !isIdentifiedConversation(conv);
    const pathKind = getConversationPathKind(conv);

    return (
      <article
        key={conv.id}
        className={`agent-conv-card${activeConv?.id === conv.id ? ' agent-conv-card--active' : ''}`}
      >
        <div className="agent-conv-card__head">
          <div className="agent-conv-card__title">
            {displayName(conv)}
            {pathKind === 'seller' && (
              <span className="agent-conv-card__badge agent-conv-card__badge--seller">Seller</span>
            )}
            {pathKind !== 'seller' && (
              <span className="agent-conv-card__badge agent-conv-card__badge--buyer">Buyer</span>
            )}
            {conv.is_registered_client && (
              <span className="agent-conv-card__badge">Saved search</span>
            )}
            {!conv.is_registered_client && conv.lead_first_name && (
              <span className="agent-conv-card__badge" style={{ color: 'var(--success)' }}>Lead</span>
            )}
          </div>
          <div className="agent-conv-card__meta">
            {conv.client_email && <div>{conv.client_email}</div>}
            <div>
              {new Date(conv.started_at).toLocaleDateString()}
              {' · '}{conv.message_count || 0} messages
            </div>
          </div>
        </div>

        {conv.summary ? (
          <>
            <p className="agent-conv-card__summary">{conv.summary}</p>
            {actionItems.length > 0 && (
              <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 12, color: 'var(--text-muted)' }}>
                {actionItems.slice(0, 3).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}
          </>
        ) : (conv.message_count || 0) < 2 ? (
          <p className="agent-conv-card__summary agent-conv-card__summary--pending">
            Too few messages for a summary yet.
          </p>
        ) : (
          <p className="agent-conv-card__summary agent-conv-card__summary--pending">No summary yet.</p>
        )}

        <div className="agent-conv-card__actions">
          {anonymous ? (
            <>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => openQa(conv)}>
                <HelpCircle size={14} /> Ask about this chat
              </button>
              <button type="button" className="agent-conv-card__link" onClick={() => openTranscript(conv)}>
                View full transcript
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => openTranscript(conv)}>
                <ScrollText size={14} /> View transcript
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => openQa(conv)}>
                <HelpCircle size={14} /> Ask about this chat
              </button>
            </>
          )}
        </div>
      </article>
    );
  };



  return (

    <div>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>

        <div>

          <h2>Conversations</h2>

          <p>
            {inboxTab === 'identified'
              ? 'People you can follow up with — saved searches, seller consults, leads, and showings.'
              : 'Visitors who chatted without contact details — summaries only; use for market insight.'}
            {' '}Copy your public chat link on Branding. Buyers and sellers use the same URL.
          </p>

        </div>

        {(loadingList || conversations.length > 0) && (
          <button className="btn btn-primary" onClick={startNewConversation} disabled={starting}>
            <Plus size={15} /> {starting ? 'Starting…' : 'New test chat'}
          </button>
        )}

      </div>



      {detailMode && activeConv ? (

        <>

          <button type="button" className="agent-chat-back" onClick={closeDetail}>

            <ArrowLeft size={16} /> Back to all conversations

          </button>



          <div className="agent-chat-detail">

            <div className="agent-conv-card agent-conv-card--active" style={{ maxHeight: 'none' }}>

              <div className="agent-conv-card__head">

                <div className="agent-conv-card__title">

                  {displayName(activeConv)}

                  {activeConv.is_registered_client && (

                    <span className="agent-conv-card__badge">Saved search</span>

                  )}

                </div>

                <div className="agent-conv-card__meta">

                  {activeConv.client_email && <div>{activeConv.client_email}</div>}

                  {new Date(activeConv.started_at).toLocaleString()}

                  {' · '}{activeConv.message_count || 0} messages

                </div>

              </div>

              {activeConv.summary ? (

                <p className="agent-conv-card__summary" style={{ WebkitLineClamp: 'unset' }}>

                  {activeConv.summary}

                </p>

              ) : (

                <p className="agent-conv-card__summary agent-conv-card__summary--pending">No summary yet.</p>

              )}

              <div className="agent-conv-card__actions">
                {!isIdentifiedConversation(activeConv) ? (
                  <>
                    <button
                      type="button"
                      className={`btn btn-sm ${detailMode === 'qa' ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => openQa(activeConv)}
                    >
                      <HelpCircle size={14} /> Ask about this chat
                    </button>
                    <button
                      type="button"
                      className="agent-conv-card__link"
                      onClick={() => openTranscript(activeConv)}
                    >
                      View full transcript
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className={`btn btn-sm ${detailMode === 'transcript' ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => openTranscript(activeConv)}
                    >
                      <ScrollText size={14} /> Full transcript
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${detailMode === 'qa' ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => openQa(activeConv)}
                    >
                      <HelpCircle size={14} /> Ask about this chat
                    </button>
                  </>
                )}
              </div>

            </div>



            {detailMode === 'transcript' && (

              <div className="agent-chat-detail__panel">

                <div className="agent-chat-detail__header">

                  <span style={{ fontWeight: 600, fontSize: 14 }}>Full transcript</span>

                </div>

                <div className="chat-messages" style={{ flex: 1 }}>

                  {messages.map(msg => (

                    <div key={msg.id} className={`message ${msg.role}`}>

                      <div className="message-avatar">

                        {msg.role === 'assistant' ? <Bot size={16} color="white" /> : <User size={16} />}

                      </div>

                      <div className="message-bubble">{msg.content}</div>

                    </div>

                  ))}

                  {sending && (

                    <div className="message assistant">

                      <div className="message-avatar"><Bot size={16} color="white" /></div>

                      <div className="typing-indicator">

                        <div className="typing-dot" /><div className="typing-dot" /><div className="typing-dot" />

                      </div>

                    </div>

                  )}

                  <div ref={messagesEndRef} />

                </div>

                {(activeConv.lead_id || activeConv.agent_client_id || activeConv.is_registered_client) ? (
                  <div className="chat-input-area" style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    This is a buyer&apos;s chat. Read it here, and reply by email or phone.
                  </div>
                ) : (
                <div className="chat-input-area">

                  <textarea

                    className="chat-input"

                    placeholder="Reply as agent test chat…"

                    value={input}

                    onChange={e => setInput(e.target.value)}

                    onKeyDown={handleKeyDown}

                    rows={1}

                  />

                  <button className="btn btn-primary" onClick={sendMessage} disabled={!input.trim() || sending}>

                    <Send size={15} />

                  </button>

                </div>
                )}

              </div>

            )}



            {detailMode === 'qa' && (

              <div className="agent-chat-detail__panel">

                <div className="agent-chat-detail__header">

                  <span style={{ fontWeight: 600, fontSize: 14 }}>Ask about this conversation</span>

                </div>

                <div className="agent-chat-qa-panel">

                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 14px', lineHeight: 1.5 }}>

                    {aiName} reads the full transcript and answers your question — e.g. budget, timeline, or next steps.

                  </p>

                  {qaHistory.map((qa, i) => (

                    <div key={qa.id || i} style={{ marginBottom: 16 }}>

                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-bright)', marginBottom: 6 }}>

                        Q: {qa.question}

                      </div>

                      <div style={{

                        fontSize: 13, color: 'var(--text-secondary)', padding: '10px 14px',

                        background: 'var(--bg-hover)', border: '1px solid var(--border)', borderRadius: 8, lineHeight: 1.65,

                      }}>

                        {qa.answer}

                      </div>

                    </div>

                  ))}

                  <div style={{ display: 'flex', gap: 10 }}>

                    <input

                      type="text"

                      value={qaQuestion}

                      onChange={e => setQaQuestion(e.target.value)}

                      onKeyDown={e => { if (e.key === 'Enter') askQuestion(); }}

                      placeholder="e.g. What budget did they mention?"

                      disabled={qaAsking}

                      style={{

                        flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border)',

                        borderRadius: 8, padding: '10px 14px', color: 'var(--text-primary)', fontSize: 13,

                      }}

                    />

                    <button className="btn btn-primary btn-sm" onClick={askQuestion} disabled={!qaQuestion.trim() || qaAsking}>

                      {qaAsking ? 'Asking…' : 'Ask'}

                    </button>

                  </div>

                </div>

              </div>

            )}

          </div>

        </>

      ) : (

        <>
          <div className="agent-chat-tabs">
            <button
              type="button"
              className={pathFilter === 'all' ? 'active' : ''}
              onClick={() => setPathFilterWithUrl('all')}
            >
              All
              <span className="agent-chat-tabs__count">({conversations.length})</span>
            </button>
            <button
              type="button"
              className={pathFilter === 'buyer' ? 'active' : ''}
              onClick={() => setPathFilterWithUrl('buyer')}
            >
              Buyers
              <span className="agent-chat-tabs__count">({buyerCount})</span>
            </button>
            <button
              type="button"
              className={pathFilter === 'seller' ? 'active' : ''}
              onClick={() => setPathFilterWithUrl('seller')}
            >
              Sellers
              <span className="agent-chat-tabs__count">({sellerCount})</span>
            </button>
          </div>

          <div className="agent-chat-tabs" style={{ marginTop: 0 }}>
            <button
              type="button"
              className={inboxTab === 'identified' ? 'active' : ''}
              onClick={() => setInboxTabWithUrl('identified')}
            >
              Leads & registered
              <span className="agent-chat-tabs__count">({identifiedConvs.length})</span>
            </button>
            <button
              type="button"
              className={inboxTab === 'anonymous' ? 'active' : ''}
              onClick={() => setInboxTabWithUrl('anonymous')}
            >
              Anonymous visitors
              <span className="agent-chat-tabs__count">({anonymousConvs.length})</span>
            </button>
          </div>

          <div className="agent-chat-inbox">
            {loadingList ? (
              <div className="card empty-state"><div className="spinner" /></div>
            ) : conversations.length === 0 ? (
              <div className="card empty-state">
                <MessageSquare size={32} color="var(--text-muted)" />
                <p>No conversations yet. Share your chat link or start a test chat.</p>
                <button className="btn btn-primary" onClick={startNewConversation} disabled={starting}>
                  <Plus size={15} /> Start test chat
                </button>
              </div>
            ) : filteredConvs.length === 0 ? (
              <div className="card empty-state">
                <p>
                  {inboxTab === 'identified'
                    ? 'No identified contacts yet — they appear when someone registers a saved search or books a showing.'
                    : 'No anonymous visitor chats in this list.'}
                </p>
              </div>
            ) : (
              <>
                {inboxTab === 'anonymous' && (
                  <p className="agent-chat-inbox-hint">
                    These visitors did not register or leave contact info. Read the summary or ask {aiName} a question —
                    you do not need to open every transcript. Patterns here can inform your market, not your call list.
                  </p>
                )}
                {filteredConvs.map(renderInboxCard)}
              </>
            )}
          </div>
        </>

      )}

    </div>

  );

}


